import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Box,
    Typography,
    Grid,
    CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { useCreateErpDataMutation } from "store/slices/apis/erpDataSlice";
import { useParams } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const UploadErpData = (props) => {
    const { open, handleClose } = props;
    const { projectId } = useParams();
    const [isDragging, setIsDragging] = useState(false);

    const dispatch = useDispatch();

    const [createErpData, { isLoading }] = useCreateErpDataMutation();

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e, setFieldValue) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];

        if (!droppedFile) return;

        const allowedExtensions = [".xlsx", ".xls", ".csv"];

        const fileName = droppedFile.name.toLowerCase();

        const isValidFile = allowedExtensions.some((ext) =>
            fileName.endsWith(ext)
        );

        if (!isValidFile) {
            openNotification(
                "Only Excel files (.xlsx, .xls, .csv) are allowed",
                "error"
            );
            return;
        }

        setFieldValue("excelFile", droppedFile);
    };

    const validationSchema = Yup.object({
        excelFile: Yup.mixed().required("Excel file is required"),
    });

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: "alert",
                alert: {
                    color: type,
                },
                close: false,
            })
        );

        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const formData = new FormData();
            formData.append("file", values.excelFile);
            formData.append("ProjectId", projectId);

            await createErpData({ body: formData }).unwrap();
            openNotification("Excel uploaded successfully", "success");
            resetForm();
            handleClose();

        } catch (error) {
            openNotification(
                error?.data?.message || error?.data?.msg || "Failed to upload excel",
                "error"
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    backgroundColor: "#FFFFFF",
                    width: "100%",
                    overflowX: "hidden",
                },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #ddd",
                    backgroundColor: "#ECECEC",
                    pb: 2,
                    pt: 3,
                    width: "100%",
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: "27px",
                            lineHeight: "100%",
                            color: "#140F36",
                            fontFamily: "Outfit, sans-serif",
                        }}
                    >
                        Upload Excel File
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Formik
                    initialValues={{
                        excelFile: null,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <Typography sx={labelTextStyle}>
                                        Upload Excel File
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: "2px dashed #E5E7EB",
                                            borderRadius: "3px",
                                            p: 5,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            borderColor: isDragging ? "#00344C" : "#E5E7EB",
                                            borderStyle: isDragging ? "solid" : "dashed",
                                            // backgroundColor: isDragging
                                            //     ? "rgba(0, 52, 76, 0.08)"
                                            //     : formik.values.file
                                            //         ? "rgba(0, 52, 76, 0.04)"
                                            //         : "transparent",
                                            "&:hover": {
                                                borderColor: "#00344C",
                                                backgroundColor: "rgba(0, 52, 76, 0.02)",
                                            },
                                        }}
                                        component="label"
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, setFieldValue)}
                                    >


                                        <input
                                            type="file"
                                            hidden
                                            accept=".xlsx,.xls,.csv"
                                            onChange={(event) => {
                                                setFieldValue(
                                                    "excelFile",
                                                    event.currentTarget.files[0]
                                                );
                                            }}
                                        />

                                        <CloudUploadIcon
                                            sx={{
                                                fontSize: 45,
                                                color: "#5F5F5F",
                                                mb: 1,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: "15px",
                                                fontFamily: "Outfit, sans-serif",
                                            }}
                                        >
                                            Click to upload excel file
                                        </Typography>

                                        {values?.excelFile && (
                                            <Typography
                                                sx={{
                                                    mt: 1,
                                                    fontSize: "14px",
                                                    color: "#1976d2",
                                                    wordBreak: "break-all",
                                                }}
                                            >
                                                {values?.excelFile?.name}
                                            </Typography>
                                        )}
                                    </Box>

                                    {touched.excelFile && errors.excelFile && (
                                        <Typography
                                            sx={{
                                                color: "#d32f2f",
                                                fontSize: "12px",
                                                mt: 0.5,
                                            }}
                                        >
                                            {errors.excelFile}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: 2,
                                            mt: 2,
                                        }}
                                    >
                                        <Button onClick={handleClose}
                                            sx={{
                                                textTransform: "none",
                                                color: "#F15744",
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                borderRadius: "25px",
                                                fontWeight: 500,
                                                px: "20px",
                                                py: "15px",
                                            }}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            // loading={isLoading}
                                            sx={{
                                                backgroundColor: "#00344C",
                                                borderRadius: "25px",
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                color: "#FFFFFF",
                                                px: "20px",
                                                py: "15px",
                                                display: "flex",
                                                gap: 1,
                                                "&.Mui-disabled": {
                                                    backgroundColor: "#00344C",
                                                    color: "#fff",
                                                    opacity: 0.7,
                                                },
                                            }}
                                            disabled={isLoading}
                                        >
                                            {isLoading && <CircularProgress size={16} color="inherit" />}
                                            Save
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog >
    );
};

export default UploadErpData;